import React, { useEffect, useRef, useState } from "react";
import VaultQuestionPerformanceChart from "./VaultQuestionPerformanceChart";
import TierSummaryChart from "./TierSummaryChart";
import AnalyticsSection from "./AnalyticsSection";
import PanelHeadline, { Fig } from "./PanelHeadline";
import { vaultAnalytics } from "../services/analyticsService";

const TIERS = [
  { label: "All Tiers", value: "all" },
  { label: "Ice Breaker", value: "iceBreaker" },
  { label: "Deeper Dive", value: "deeperDive" },
  { label: "Wild Card", value: "wildCard" },
  { label: "Connection Goals", value: "connectionGoals" },
];

const AnalyticsPage = ({ startLoading, stopLoading }) => {
  const [tier, setTier] = useState("all");
  const [data, setData] = useState([]);
  const [tierEvents, setTierEvents] = useState([]);
  // const [loading, setLoading] = useState(false);
  const isFirstLoad = useRef(true);


  useEffect(() => {
    fetchAnalytics();
  }, [tier]);

  const fetchAnalytics = async () => {
    try {
      if (isFirstLoad.current) startLoading();
      const res = await vaultAnalytics({ tier });
      setData(res?.data?.data || []);
      setTierEvents(res?.data?.tierEvents || []);
    } catch (err) {
      console.error("Failed to load vault analytics", err);
    } finally {
      if (isFirstLoad.current) {
        stopLoading();
        isFirstLoad.current = false;
      }
    }
  };

  return (
    <AnalyticsSection
      id="vaultq"
      icon="mdi:comment-question-outline"
      title="Vault Question Performance"
      basis={
        tier === "all"
          ? "Tier-level completion against drop-off. A skip is one question tapped past, never a tier: the three tiers are always taken in order."
          : "Question-level completion against drop-off. A skip is one question tapped past, never a tier: the three tiers are always taken in order."
      }
      right={
        <select
          className="form-select w-auto"
          value={tier}
          onChange={(e) => setTier(e.target.value)}
          aria-label="Tier"
        >
          {TIERS.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
      }
    >
      <>
        {(() => {
          const t = data.reduce(
            (a, q) => ({
              views: a.views + (q.views || 0),
              answered: a.answered + (q.answered || 0),
              skipped: a.skipped + (q.skipped || 0),
            }),
            { views: 0, answered: 0, skipped: 0 }
          );
          const rateIsImpossible = t.views > 0 && t.answered > t.views;
          return (
            <PanelHeadline
              failed={rateIsImpossible}
              failedText={`The answer rate cannot be shown: ${t.answered.toLocaleString()} answers against ${t.views.toLocaleString()} views is over 100%, so the viewed event is under-firing.`}
              empty={!data.length}
              emptyText="No question events recorded yet."
            >
              Skipping is rare: <Fig>{t.skipped.toLocaleString()}</Fig> skips across{" "}
              <Fig>{t.answered.toLocaleString()}</Fig> answers.
            </PanelHeadline>
          );
        })()}
        {data.length > 0 && (() => {
          const totals = data.reduce(
            (acc, q) => ({
              views: acc.views + (q.views || 0),
              answered: acc.answered + (q.answered || 0),
              skipped: acc.skipped + (q.skipped || 0),
            }),
            { views: 0, answered: 0, skipped: 0 }
          );
          const eventTotals = tierEvents.reduce(
            (acc, t) => ({
              funFacts: acc.funFacts + (t.funFactViewed || 0),
              tiersCompleted: acc.tiersCompleted + (t.tierCompleted || 0),
            }),
            { funFacts: 0, tiersCompleted: 0 }
          );
          const completion = totals.views
            ? ((totals.answered / totals.views) * 100).toFixed(1)
            : 0;
          const stats = [
            { label: "Question Views", value: totals.views },
            { label: "Answered", value: totals.answered },
            { label: "Skipped", value: totals.skipped },
            { label: "Fun Facts Viewed", value: eventTotals.funFacts },
            { label: "Tiers Completed", value: eventTotals.tiersCompleted },
            { label: "Overall Completion", value: `${completion}%` },
          ];
          return (
            <div className="row g-2 mb-3">
              {stats.map((s) => (
                <div className="col" key={s.label}>
                  <div className="border rounded text-center py-2 px-1 h-100">
                    <div className="fw-bold">{s.value}</div>
                    <small className="text-muted">{s.label}</small>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}

        {tier === "all" ? (
          <TierSummaryChart data={data} />
        ) : (
          <VaultQuestionPerformanceChart data={data} />
        )}
      </>
    </AnalyticsSection>
  );
};

export default AnalyticsPage;
